import { useLazyQuery, useMutation } from "@apollo/client";
import {
  HandThumbDownIcon,
  HandThumbUpIcon,
  PhotoIcon,
  UserIcon,
} from "@heroicons/react/24/solid";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  addDays,
  addHours,
  endOfDay,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionButton from "../../components/ActionButton/ActionButton";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import PhotoHashBadge from '../../components/PhotoHashBadge/PhotoHashBadge';
import PlatformIcon from "../../components/PlatformIcon/PlatformIcon";
import Badge from "../../components/Styled/Badge";
import { Row } from "../../components/Styled/Container";
import {
  MediaActionRow,
  MediaActions,
  MediaBadges,
  MediaCard,
  MediaCornerActions,
  MediaFrame,
  MediaGrid,
  MediaIconChip,
  MediaImage,
  MediaTop,
} from "../../components/Styled/Media";
import type { PhotoWithHash } from '../../graphql/photoHash';
import { APPROVE_PHOTO } from "../../graphql/mutations-gql";
import { UNVERIFIED_PHOTOS } from "../../graphql/queries-gql";

const PAGE_SIZE = 50;

enum DateRange {
  Today,
  Yesterday,
  ThisMonth,
  Last24Hours,
  Last3Days,
  Last7Days,
  Last30Days,
}

const UnverifiedPhotos = () => {
  const [fromDate, setFromDate] = useState<Date>(addHours(new Date(), -24));
  const [toDate, setToDate] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState<DateRange>(DateRange.Last24Hours);

  const [fetchPhotos, { loading, error }] = useLazyQuery(UNVERIFIED_PHOTOS, {
    fetchPolicy: "network-only",
    variables: {
      args: {
        paging: {
          type: "page",
          pageArgs: {
            page: 1,
            limit: PAGE_SIZE,
          },
        },
        fromDate,
        toDate,
      },
    },
    onCompleted: (data) => {
      console.log("fetchPhotos onCompleted", data);
      setPhotos(data.unverifiedPhotos.nodes);
    },
    onError: (error) => {
      console.log("fetchPhotos onError", error);
    },
  });

  useEffect(() => {
    fetchPhotos();
  }, [fromDate, toDate, fetchPhotos]);

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    switch (range) {
      case DateRange.Today:
        setFromDate(startOfDay(new Date()));
        setToDate(new Date());
        break;
      case DateRange.Yesterday:
        setFromDate(startOfDay(addDays(new Date(), -1)));
        setToDate(endOfDay(addDays(new Date(), -1)));
        break;
      case DateRange.ThisMonth:
        setFromDate(startOfMonth(new Date()));
        setToDate(new Date());
        break;
      case DateRange.Last24Hours:
        setFromDate(addHours(new Date(), -24));
        setToDate(new Date());
        break;
      case DateRange.Last3Days:
        setFromDate(addDays(new Date(), -3));
        setToDate(new Date());
        break;
      case DateRange.Last7Days:
        setFromDate(addDays(new Date(), -7));
        setToDate(new Date());
        break;
      case DateRange.Last30Days:
        setFromDate(addDays(new Date(), -30));
        setToDate(new Date());
        break;
    }
  };

  const [photos, setPhotos] = useState<PhotoWithHash[]>([]);
  const [approvePhoto] = useMutation(APPROVE_PHOTO);
  const navigate = useNavigate();

  const showImage = (src: string) => {
    //window.location.href = src;
    window.open(src, "_blank");
  };

  // const showMore = () => {
  //   fetchPhotos({
  //     variables: {
  //       paging: {
  //         type: "page",
  //         pageArgs: {
  //           page: page?.nextPage,
  //           limit: PAGE_SIZE,
  //         },
  //       },
  //     },
  //     onCompleted: (data) => {
  //       console.log("fetchPhotos onCompleted", data);
  //       setPage(data.unverifiedPhotos.paging);
  //       setPhotos([...photos, ...data.unverifiedPhotos.nodes]);
  //     },
  //     onError: (error) => {
  //       console.log("fetchPhotos onError", error);
  //     },
  //   });
  // };

  const handleApprove = (value: boolean, uploadId: string) => {
    approvePhoto({
      variables: {
        uploadId,
        value,
      },
      onCompleted: (data) => {
        console.log("approvePhoto onCompleted", data);
      },
      onError: (error) => {
        console.log("approvePhoto onError", error);
      },
    });

    // Lets immediately remove the photo from the list so it doesnt feel laggy
    const newPhotos = photos.filter((photo) => photo.uploadId !== uploadId);
    setPhotos(newPhotos);
  };

  return (
    <Page
      title="Public photos"
      description="Unverified public uploads. Hover a photo to approve or reject it."
      badge={
        photos.length > 0 && (
          <Badge $tone="primary">{photos.length} to review</Badge>
        )
      }
      actions={
        <ToggleButtonGroup
          value={dateRange}
          exclusive
          onChange={(_, value) =>
            value !== null && handleDateRangeChange(value)
          }
          size="small"
        >
          <ToggleButton value={DateRange.Today}>Today</ToggleButton>
          <ToggleButton value={DateRange.Yesterday}>Yesterday</ToggleButton>
          <ToggleButton value={DateRange.ThisMonth}>This month</ToggleButton>
          <ToggleButton value={DateRange.Last24Hours}>24 hours</ToggleButton>
          <ToggleButton value={DateRange.Last3Days}>3 days</ToggleButton>
          <ToggleButton value={DateRange.Last7Days}>7 days</ToggleButton>
          <ToggleButton value={DateRange.Last30Days}>30 days</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {error && <ErrorAlert error={error} />}

      {!loading && !error && photos.length === 0 && (
        <EmptyState
          Icon={PhotoIcon}
          title="All caught up"
          description="No unverified public photos in this period."
        />
      )}

      {photos && photos.length > 0 && (
        <MediaGrid $min={250}>
          {photos.map((photo) => (
            <MediaCard key={photo.uploadId}>
              <MediaFrame onClick={() => showImage(photo.url)}>
                <MediaImage src={photo.url} alt="" loading="lazy" />
                <MediaTop>
                  <span />
                  <MediaBadges $align="end">
                    <PhotoHashBadge hasHash={photo.hasPhotoHash} />
                    {photo.platform && (
                      <MediaIconChip title={photo.platform}>
                        <PlatformIcon platform={photo.platform} />
                      </MediaIconChip>
                    )}
                  </MediaBadges>
                </MediaTop>
                <MediaCornerActions>
                  <ActionButton
                    label="Open user"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/user/${photo.uploadById}`);
                    }}
                  >
                    <UserIcon />
                  </ActionButton>
                </MediaCornerActions>
                <MediaActions>
                  <MediaActionRow>
                    <ActionButton
                      label="Approve"
                      tone="approve"
                      size={48}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(true, photo.uploadId);
                      }}
                    >
                      <HandThumbUpIcon />
                    </ActionButton>
                    <ActionButton
                      label="Reject"
                      tone="reject"
                      size={48}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(false, photo.uploadId);
                      }}
                    >
                      <HandThumbDownIcon />
                    </ActionButton>
                  </MediaActionRow>
                </MediaActions>
              </MediaFrame>
            </MediaCard>
          ))}
        </MediaGrid>
      )}

      {loading && (
        <Row justifyContent="center">
          <Loader />
        </Row>
      )}
    </Page>
  );
};
export default UnverifiedPhotos;
